/* eslint-disable react/no-danger */
import React, { useId } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { useTranslation } from 'react-i18next';
import { parseLongDescription } from '@/utils/markdown-util';
import { render } from '@/utils/story-composer';
import Wave from '@/components/elements/wave';
import LibraryBreadcrumb from '@/components/library-elements/library-breadcrumb';
import ThumborImageService from '@/utils/thumbor-image-service';
import ThumborFit from '@/enums/thumbor-fit';

// Improvements: Add lazy-loading to these imports (loadable-component-ssr / React.lazy)
import KeyPoints from '@/components/library-elements/mdx-components/keypoints';
import KeyPoint from '@/components/library-elements/mdx-components/keypoint';
import MdxWave from '@/components/library-elements/mdx-components/mdx-wave';
import MdxRow from '@/components/library-elements/mdx-components/mdx-row';
import MdxColumn from '@/components/library-elements/mdx-components/mdx-column';
import MdxVideo from '@/components/library-elements/mdx-components/mdx-video';

export default function LibraryItem({
	page,
	locale,
	hasMdxDescription,
	children,
	storyblokComponents,
	discoverMoreItems,
}) {
	const { i18n } = useTranslation();
	const thumborServiceInstance = ThumborImageService.getInstance();

	const { model } = page;
	const isGlobalLibrary = i18n.language !== 'nl-nl';

	const renderMDX = () => {
		const shortcodes = {
			KeyPoints,
			KeyPoint,
			Wave: MdxWave,
			Row: MdxRow,
			Column: MdxColumn,
			Video: MdxVideo,
		};

		return (
			<div className="[&>:not(.mdx-wave)]:container">
				<MDXProvider components={shortcodes}>{children}</MDXProvider>
			</div>
		);
	};

	let redirectUrl = `https://teacher.gynzy.com/#/${locale}/docent/index/library/item/${model.id}`;
	const isNumberRegex = /^\d*$/gm;
	const isLesson = model.type_name !== 'tool' && !isNumberRegex.test(model.reference_id);

	if (model.language_code !== 'nl') {
		if (isLesson) {
			redirectUrl = `https://www.gynzy.com/teach/${locale}/board/${model.reference_id}`;
		} else {
			redirectUrl = `https://www.gynzy.com/teach/${locale}/board?content=${model.id}`;
		}
	} else if (isLesson) {
		redirectUrl = `https://teacher.gynzy.com/#/${locale}/docent/board/${model.reference_id}`;
	} else {
		redirectUrl = `https://teacher.gynzy.com/#/${locale}/docent/index/library/item/${model.id}`;
	}

	const registerUrl = new URL(`https://www.gynzy.com/account/${model.language_code}/register`);
	registerUrl.searchParams.set('redirect_url', redirectUrl);

	const tryNowLink = {
		linktype: 'url',
		url: model.language_code !== 'nl' ? redirectUrl : registerUrl.href, // skip registration for the global pages
	};

	const renderFallback = () => {
		const parsedLongDescription = parseLongDescription(model.long_description);

		const descriptionDom = parsedLongDescription.map(data => (
			<div key={`${useId()}`}>
				<h2 className="font-sans text-2xl leading-8 font-semibold mt-2 md:line-clamp-2">{data.title}</h2>
				<p dangerouslySetInnerHTML={{ __html: data.description }} />
			</div>
		));

		return (
			<>
				<Wave fillColor="MARINER_EXTRA_LIGHT" />
				<div className="bg-mariner-extra-light">
					<div className="container pb-20 pt-10">{descriptionDom}</div>
				</div>
				<Wave backgroundColor="MARINER_EXTRA_LIGHT" />
			</>
		);
	};

	const thumbnailPath = thumborServiceInstance.getThumborUrlForImage(model.thumbnail_path, 320, 288, {
		fit: ThumborFit.IN,
	});

	// Only show the top-level category
	const breadcrumbCategories = page.categories[0]
		? [page.categories[0].parentCategories[0] ? page.categories[0].parentCategories[0] : page.categories[0]]
		: [];

	const renderNLHeader = () => (
		<>
			<div className="relative bg-mariner-extra-light h-72">
				<div className="absolute pt-20 left-0 right-0 top-50 flex justify-center items-center">
					{model.thumbnail_path ? <img src={thumbnailPath} alt={model.name} className="border max-h-72" /> : null}
				</div>
			</div>
			<Wave backgroundColor="MARINER_EXTRA_LIGHT" />

			<LibraryBreadcrumb
				className="mt-10 py-6"
				locale={locale}
				crumbs={breadcrumbCategories}
				currentCrumb={model.name}
			/>

			<div className="prose container pt-0 pb-10">
				<h1 className="text-5xl text-center pb-4">{model.name}</h1>
				<p className="text-center">{model.description}</p>
			</div>

			{model.type_name === 'tool' && storyblokComponents.tryToolButton ? (
				<div className="container flex justify-center pb-4">
					{render(storyblokComponents.tryToolButton.content, { buttonLinkOverwrite: tryNowLink })}
				</div>
			) : null}

			{model.type_name !== 'tool' && storyblokComponents.tryLessonButton ? (
				<div className="container flex justify-center pb-4">
					{render(storyblokComponents.tryLessonButton.content, { buttonLinkOverwrite: tryNowLink })}
				</div>
			) : null}
		</>
	);

	const renderGlobalHeader = () => (
		<div className="relative overflow-hidden bg-mariner-extra-light">
			<div className="absolute right-0 top-9 invisible lg:visible">
				<img src="/images/classroom-window-frame.png" alt="Window frame" className="m-0" />
			</div>

			<LibraryBreadcrumb
				className="pt-10 pb-7"
				locale={locale}
				crumbs={breadcrumbCategories}
				currentCrumb={model.name}
			/>

			<div className="container prose grid grid-cols-1 lg:grid-cols-2">
				<div className="pb-10 lg:pb-0">
					<h1 className="text-5xl pb-4">{model.name}</h1>
					<p>{model.description}</p>

					{model.type_name === 'tool' && storyblokComponents.tryToolButton
						? render(storyblokComponents.tryToolButton.content, { buttonLinkOverwrite: tryNowLink })
						: null}

					{model.type_name !== 'tool' && storyblokComponents.tryLessonButton
						? render(storyblokComponents.tryLessonButton.content, { buttonLinkOverwrite: tryNowLink })
						: null}
				</div>

				<div className="relative flex justify-end items-end lg:min-h-56 pr-12 md:pr-24 lg:pr-20">
					<div className="relative flex justify-center items-center library-item__classroom-interactive-whiteboard min-w-80 w-80 md:w-[23rem] xl:w-[30rem] h-48 md:h-56 xl:h-72">
						<div className="h-full w-full flex justify-center items-center pt-10 pb-11">
							{model.thumbnail_path ? (
								<img src={thumbnailPath} alt={model.name} className="max-w-full max-h-full m-0 rounded-lg" />
							) : null}
						</div>
						<div className="absolute -right-16 md:-right-24 top-20 md:top-28">
							<img src="/images/classroom-teacher.png" alt="Teacher" className="m-0 h-64 md:h-80 xl:h-96" />
						</div>
						<div className="absolute -left-44 md:-left-56 xl:-left-80 -bottom-36 md:-bottom-44 xl:-bottom-48">
							<img src="/images/classroom-kids.png" alt="Kids" className="m-0 w-96 md:w-[31rem] xl:w-[36rem]" />
						</div>
					</div>
				</div>
			</div>

			<div className="relative h-36 md:h-44 xl:h-48 border-b-8 border-sky-light" />
		</div>
	);

	return (
		<div>
			{isGlobalLibrary ? renderGlobalHeader() : renderNLHeader()}

			{storyblokComponents.gynzyUsageBanner ? <>{render(storyblokComponents.gynzyUsageBanner.content)}</> : null}

			{storyblokComponents.libraryAboveDescription ? render(storyblokComponents.libraryAboveDescription.content) : null}

			<div className="prose">{hasMdxDescription ? renderMDX() : renderFallback()}</div>

			{storyblokComponents.libraryBelowDescription ? render(storyblokComponents.libraryBelowDescription.content) : null}

			{storyblokComponents.libraryTestimonials ? render(storyblokComponents.libraryTestimonials.content) : null}

			{storyblokComponents.discoverMoreTools
				? render(storyblokComponents.discoverMoreTools.content, {
						currentContentItem: model,
						locale,
						possibleItems: discoverMoreItems,
				  })
				: null}

			{storyblokComponents.whoIsGynzyBlock ? (
				<div className="container pb-20">{render(storyblokComponents.whoIsGynzyBlock.content)}</div>
			) : null}
		</div>
	);
}
